import * as ws from 'ws';
import getLogger from '../../../logger.ts';
import {IOGateway} from '@interopio/gateway';
import GatewayEncoders = IOGateway.Encoding;

import type {ServerWebSocketHandler} from '../../../../types/web/server';

const logger = getLogger('mesh.ws.broker');

type Command =
    // client to broker
    { type: 'hello', 'node-id': string }
    | { type: 'bye', 'node-id': string }
    | { type: 'data', from: string, to: 'all' | string}
    // broker to client
    | { type: 'node-added', 'node-id': string, 'new-node': string }
    | { type: 'node-removed', 'node-id': string, 'removed-node': string }

    ;


function broadcastNodeAdded(nodes: NodeSockets, newSocket: ws.WebSocket, newNodeId: string) {
    Object.entries(nodes.nodes).forEach(([nodeId, socket]) => {
        if (nodeId !== newNodeId) {
            newSocket.send(codec.encode({type: 'node-added', 'node-id': newNodeId, "new-node": nodeId}));
            socket.send(codec.encode({type: 'node-added', 'node-id': nodeId, "new-node": newNodeId}));
        }
    });
}

function broadcastNodeRemoved(nodes: NodeSockets, removedNodeId: string) {
    Object.entries(nodes.nodes).forEach(([nodeId, socket]) => {
        if (nodeId !== removedNodeId) {
            socket.send(codec.encode({type: 'node-removed', 'node-id': nodeId, "removed-node": removedNodeId}));
        }
    });
}

function onOpen(connectedNodes: NodeSockets, logPrefix: string) {
    logger.info(`${logPrefix}connection accepted`);
}

function onClose(connectedNodes: NodeSockets, logPrefix: string, code: number, reason: string): void {
    logger.info(`${logPrefix}connection closed [${code}](${reason})`);
    const nodeIds = connectedNodes.sockets[logPrefix];
    if (nodeIds) {
        delete connectedNodes.sockets[logPrefix];
        for (const nodeId of nodeIds) {
            delete connectedNodes.nodes[nodeId];
        }
        for (const nodeId of nodeIds) {
            broadcastNodeRemoved(connectedNodes, nodeId);
        }
    }
}

function processMessage(connectedNodes: NodeSockets, socket: ws.WebSocket, key: string, msg: Command) {
    switch (msg.type) {
        case 'hello': {
            const nodeId = msg['node-id'];
            connectedNodes.nodes[nodeId] = socket;
            connectedNodes.sockets[key] = connectedNodes.sockets[key] ?? [];
            connectedNodes.sockets[key].push(nodeId);
            logger.info(`[${key}] node ${nodeId} added.`);
            broadcastNodeAdded(connectedNodes, socket, nodeId);
            break;
        }
        case 'bye': {
            const nodeId = msg["node-id"];
            delete connectedNodes[nodeId];
            logger.info(`[${key}] node ${nodeId} removed.`);
            broadcastNodeRemoved(connectedNodes, nodeId);
            break;
        }
        case 'data': {
            const sourceNodeId = msg.from;
            const targetNodeId = msg.to;
            if ('all' === targetNodeId) {
                Object.entries(connectedNodes.nodes).forEach(([nodeId, socket]) => {
                    if (nodeId !== sourceNodeId) {
                        socket.send(codec.encode(msg));
                    }
                });
            }
            else {
                const socket = connectedNodes.nodes[targetNodeId];
                if (socket) {
                    socket.send(codec.encode(msg));
                }
                else {
                    logger.warn(`unable to send to node ${targetNodeId} message ${JSON.stringify(msg)}`);
                }
            }
            break;
        }
        default: {
            logger.warn(`[${key}] ignoring unknown message ${JSON.stringify(msg)}`);
            break;
        }
    }
}

const codec = GatewayEncoders.transit<Command>({
    keywordize: new Map<string, GatewayEncoders.KeywordizeCommand>([
        ['/type', '*'],
        ['/message/body/type', '*'],
        ['/message/origin', '*'],
        ['/message/receiver/type', '*'],
        ['/message/source/type', '*'],
        ['/message/body/type', '*'],
    ])
});

function onMessage(connectedNodes: NodeSockets, socket: ws.WebSocket, logPrefix: string, msg: string): void {
    try {
        const decoded = codec.decode(msg);
        if (logger.enabledFor('debug')) {
            logger.debug(`${logPrefix}processing msg ${JSON.stringify(decoded)}`);
        }
        processMessage(connectedNodes, socket, logPrefix, decoded);
    } catch (ex) {
        logger.error(`${logPrefix}unable to process message`, ex);
    }
}

class WebsocketBroker {
    constructor(private readonly server: ws.WebSocketServer) {
    }

    async close() {
        this.server.close();
    }
}
type NodeSockets = {nodes: {[nodeId: string]: ws.WebSocket}, sockets: {[socket: string]: string[]}};

async function create(environment: {endpoint: string}): Promise<ServerWebSocketHandler> {
    const connectedNodes: NodeSockets = {nodes: {}, sockets: {}};
    logger.info(`mesh server is listening`);

    return async ({socket, handshake}) => {
        const logPrefix = handshake.logPrefix!;
        onOpen(connectedNodes, logPrefix);
        socket.on('error', (err: Error) => {
            logger.error(`${logPrefix}websocket error: ${err}`, err);
        });
        socket.on('message', (data, _isBinary) => {
            if (Array.isArray(data)) {
                data = Buffer.concat(data);
            }
            onMessage(connectedNodes, socket, logPrefix, data as unknown as string);
        });
        socket.on('close', (code, reason) => {
            onClose(connectedNodes, logPrefix, code, reason as unknown as string);
        });
    };
}

export default create;
